import { Avatar, Body, FieldError } from '@e2n/ui/v2';
import { Select } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/components/SelectV2';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useAbteilungsleiter } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useAbteilungsleiter/useAbteilungsleiter';
import { Abteilungsleiter } from '@/domain/mitarbeiter/features/rotationsplan/entities/Abteilungsleiter';

export function AbteilungsleiterSelect({
  onChange,
  selectedArbeitsbereichId,
  selectedAbteilungsleiter,
  showWarning = false,
}: {
  onChange: (abteilungsleiter: Abteilungsleiter) => void;
  selectedArbeitsbereichId: string | undefined;
  selectedAbteilungsleiter?: Abteilungsleiter;
  showWarning?: boolean;
}) {
  const { standorte } = useStandortContext();
  const standortIds = standorte.map((s) => s.id);

  if (!selectedArbeitsbereichId) {
    return (
      <AbteilungsleiterSelectContent
        showWarning={showWarning}
        abteilungsleiter={[]}
        onChange={onChange}
        selectedAbteilungsleiter={selectedAbteilungsleiter}
      />
    );
  }

  return (
    <AbteilungsleiterSelectWithData
      showWarning={showWarning}
      onChange={onChange}
      selectedArbeitsbereichId={selectedArbeitsbereichId}
      standortIds={standortIds}
      selectedAbteilungsleiter={selectedAbteilungsleiter}
    />
  );
}

function AbteilungsleiterSelectWithData({
  onChange,
  selectedArbeitsbereichId,
  standortIds,
  selectedAbteilungsleiter,
  showWarning,
}: {
  onChange: (abteilungsleiter: Abteilungsleiter) => void;
  selectedArbeitsbereichId: string;
  standortIds: string[];
  selectedAbteilungsleiter?: Abteilungsleiter;
  showWarning: boolean;
}) {
  const { abteilungsleiter } = useAbteilungsleiter(
    standortIds,
    selectedArbeitsbereichId,
  );

  return (
    <AbteilungsleiterSelectContent
      abteilungsleiter={abteilungsleiter}
      onChange={onChange}
      selectedAbteilungsleiter={selectedAbteilungsleiter}
      showWarning={showWarning}
    />
  );
}

function AbteilungsleiterSelectContent({
  onChange,
  abteilungsleiter,
  selectedAbteilungsleiter,
  showWarning,
}: {
  onChange: (abteilungsleiter: Abteilungsleiter) => void;
  abteilungsleiter: Abteilungsleiter[];
  selectedAbteilungsleiter?: Abteilungsleiter;
  showWarning: boolean;
}) {
  const { t } = useTranslation();
  const hasItems = abteilungsleiter.length > 0;

  return (
    <div className="flex flex-col gap-1">
      <Select<Abteilungsleiter>
        selectedOption={selectedAbteilungsleiter}
        onChange={onChange}
        drawerLabel={t(
          'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.abteilungsleitung.label',
        )}
        options={abteilungsleiter}
        getOptionLabel={(a) => a.getName()}
        emptyText={
          hasItems
            ? undefined
            : t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.abteilungsleitung.notFound',
              )
        }
        renderListItem={(a, selected) => (
          <div className="flex w-full min-w-0 items-center gap-2">
            <Avatar size="xs" type="initials" initials={a.getInitials()} />
            <Body className="min-w-0 flex-1 truncate">{a.getName()}</Body>
            {a.id === selected?.id && (
              <FontAwesomeIcon
                className="size-4 text-brand-red-200"
                icon={faCheck}
              />
            )}
          </div>
        )}
      />
      {showWarning && (
        <FieldError
          variant="warning"
          errors={[
            {
              message: t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.abteilungsleitung.reset',
              ),
            },
          ]}
        />
      )}
    </div>
  );
}
